import { useMutation, useQueryClient } from '@tanstack/react-query';
import { track } from '@/shared/analytics';
import {
  archiveBrandFonts,
  createBrandFont,
  replaceBrandFontFile,
  setFontTags,
  type BrandFontItem,
  type CreateBrandFontInput,
  type ReplaceBrandFontFileInput,
  type SetFontTagsInput,
} from './brandFont.api';
import { BRAND_FONT_QUERY_KEYS } from './queries';

/** Mirrors `useCreateBrandMedia`: invalidate by prefix rather than the exact listing. */
export function useCreateBrandFont() {
  const queryClient = useQueryClient();

  return useMutation<BrandFontItem, Error, CreateBrandFontInput>({
    mutationFn: createBrandFont,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BRAND_FONT_QUERY_KEYS.all }),
  });
}

/** Mirrors `useCreateBrandFont`: invalidate by prefix rather than the exact listing. */
export function useSetFontTags() {
  const queryClient = useQueryClient();

  return useMutation<BrandFontItem, Error, SetFontTagsInput>({
    mutationFn: setFontTags,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: BRAND_FONT_QUERY_KEYS.all }),
  });
}

export function useReplaceBrandFontFile() {
  const queryClient = useQueryClient();

  return useMutation<BrandFontItem, Error, ReplaceBrandFontFileInput>({
    mutationFn: replaceBrandFontFile,
    onSuccess: (font) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_FONT_QUERY_KEYS.all });
      track('brand_font_file_replaced', { font_id: font.id });
    },
  });
}

/**
 * Retires fonts, one or a selection spanning the library.
 *
 * No toast here, matching `useDeleteBrandMedia`: what to say depends on how many went and on
 * whether assets went with them, which the calling surface holds and this hook does not.
 *
 * One event carrying the count rather than one per font: the person made one gesture, and a
 * selection of thirty would otherwise read in analytics as thirty separate decisions.
 */
export function useDeleteBrandFonts() {
  const queryClient = useQueryClient();

  return useMutation<{ archived: number }, Error, { fontIds: readonly string[] }>({
    mutationFn: ({ fontIds }) => archiveBrandFonts(fontIds),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: BRAND_FONT_QUERY_KEYS.all });
      track('brand_fonts_deleted', { font_count: result.archived });
    },
  });
}
